import type { Channel } from 'pusher-js';

export interface PusherContextState {
  isPusherConnected: boolean;
  orgChannel: Channel | null;
}

export const defaultState: PusherContextState = {
  isPusherConnected: false,
  orgChannel: null
};

export enum PUSHER_STATES {
  TRANSCRIPTION_DONE = 'TRANSCRIPTION_UPDATED:DONE',
  TRANSCRIPTION_FAILED = 'TRANSCRIPTION_UPDATED:FAILED',
  TRANSCRIPTION_INVALID_AUDIO = 'TRANSCRIPTION_UPDATED:INVALID_AUDIO',
  TRANSCRIPTION_NO_AUDIOSTREAM = 'TRANSCRIPTION_UPDATED:NO_AUDIOSTREAM',
  TRANSCRIPTION_PROCESSING = 'TRANSCRIPTION_UPDATED:PROCESSING',
  TRANSCRIPTION_UNAVAILABLE = 'TRANSCRIPTION_UPDATED:UNAVAILABLE',
  TRANSCRIPTION_SUPPORT_RESTRICTED = 'TRANSCRIPTION_UPDATED:MULTI_LANGUAGE_SUPPORT_RESTRICTED',
  TRANSCRIPT_NOT_AVAILABLE = 'TRANSCRIPTION_UPDATED:TRANSCRIPT_NOT_AVAILABLE',
  CLIP_GENERATION_DONE = 'CLIP_GENERATION_STATUS:DONE',
  CLIP_GENERATION_FAILED = 'CLIP_GENERATION_STATUS:FAILED',
  CLIP_GENERATION_LIMIT_REACHED = 'CLIP_GENERATION_STATUS:LIMIT_REACHED',
  DOWNLOAD_DONE = 'DOWNLOAD_UPDATED:DONE',
  DOWNLOAD_FAILED = 'DOWNLOAD_UPDATED:FAILED',
  MULTIMODAL_DOWNLOAD_DONE = 'MULTIMODAL_DOWNLOAD_UPDATED:DONE',
  MULTIMODAL_DOWNLOAD_FAILED = 'MULTIMODAL_DOWNLOAD_UPDATED:FAILED',
  SOCIAL_POST_GENERATION_DONE = 'SOCIAL_POST_GENERATION_STATUS:DONE',
  BLOG_POST_GENERATION_DONE = 'BLOG_POST_GENERATION_STATUS:DONE',
  EMAIL_POST_GENERATION_DONE = 'EMAIL_POST_GENERATION_STATUS:DONE',
  TAKEAWAY_POST_GENERATION_DONE = 'TAKEAWAY_POST_GENERATION_STATUS:DONE',
  FACIAL_RECOGNITION_FAST_PROCESSING = 'FACIAL_RECOGNITION_FAST:PROCESSING',
  FACIAL_RECOGNITION_FAST_DONE = 'FACIAL_RECOGNITION_FAST:DONE',
  FACIAL_RECOGNITION_FAST_FAILED = 'FACIAL_RECOGNITION_FAST:FAILED',
  FACIAL_RECOGNITION_ACCURATE_PROCESSING = 'FACIAL_RECOGNITION_ACCURATE:PROCESSING',
  FACIAL_RECOGNITION_ACCURATE_DONE = 'FACIAL_RECOGNITION_ACCURATE:DONE',
  FACIAL_RECOGNITION_ACCURATE_FAILED = 'FACIAL_RECOGNITION_ACCURATE:FAILED',
  FTUX_CLIPS_FACIAL_RECOGNITION_DONE = 'FTUX_CLIPS_FACIAL_RECOGNITION:DONE',
  FACIAL_RECOGNITION_FULLVIDEO_PROCESSING = 'FACIAL_RECOGNITION_FULLVIDEO:PROCESSING',
  FACIAL_RECOGNITION_FULLVIDEO_DONE = 'FACIAL_RECOGNITION_FULLVIDEO:DONE',
  FACIAL_RECOGNITION_FULLVIDEO_FAILED = 'FACIAL_RECOGNITION_FULLVIDEO:FAILED',
  FTUX_FULLVIDEO_FACIAL_RECOGNITION_DONE = 'FTUX_FULLVIDEO_FACIAL_RECOGNITION:DONE',
  PUBLISH_STATUS_UPDATED_DONE = 'PUBLISH_STATUS_UPDATED:DONE',
  PUBLISH_STATUS_UPDATED_FAILED = 'PUBLISH_STATUS_UPDATED:FAILED',
  CHAPTER_GENERATION_DONE = 'CHAPTER_GENERATION_STATUS:DONE',
  CHAPTER_GENERATION_FAILED = 'CHAPTER_GENERATION_STATUS:FAILED',
  ALIGNED_INDIVIDUAL_STREAMS_GENERATED_DONE = 'ALIGNED_INDIVIDUAL_STREAMS_GENERATED:DONE',
  ALIGNED_INDIVIDUAL_STREAMS_GENERATED_FAILED = 'ALIGNED_INDIVIDUAL_STREAMS_GENERATED:FAILED',
  CAPTION_TRANSLATION_DONE = 'CAPTION_TRANSLATION_STATUS:DONE',
  CAPTION_TRANSLATION_FAILED = 'CAPTION_TRANSLATION_STATUS:FAILED',
  AI_AGENT_UPDATE = 'AI_AGENT_UPDATE',
  EVENT_MIGRATION_IN_PROGRESS = 'EVENT_MIGRATION_IN_PROGRESS',
  EVENT_MIGRATION_COMPLETED = 'EVENT_MIGRATION_COMPLETED',
  SCREENSHARE_DETECTION_DONE = 'SCREENSHARE_DETECTION:DONE',
  SCREENSHARE_DETECTION_FAILED = 'SCREENSHARE_DETECTION:FAILED',
  SCREENSHARE_DETECTION_UNAVAILABLE = 'SCREENSHARE_DETECTION:UNAVAILABLE',
  CL_HOMEPAGE_CAROUSEL_STATUS_UPDATED = 'CL_HOMEPAGE_CAROUSEL_STATUS_UPDATED'
}

export const PUSHER_STATE_MESSAGES: Record<PUSHER_STATES, string> = {
  [PUSHER_STATES.TRANSCRIPTION_DONE]:
    'Video Processing Completed! Fresh assets have been generated from your uploaded video.',
  [PUSHER_STATES.TRANSCRIPTION_FAILED]:
    'Video Processing Failed: There was an issue processing your video. Please contact our support team for assistance.',
  [PUSHER_STATES.TRANSCRIPTION_INVALID_AUDIO]:
    'Video Processing Failed: The audio is unclear in this video. Please upload a file with clearly audible speech.',
  [PUSHER_STATES.TRANSCRIPTION_NO_AUDIOSTREAM]:
    'Video Processing Failed: This video lacks audio. Please upload a file with sound.',
  [PUSHER_STATES.TRANSCRIPTION_PROCESSING]: 'Video processing: We are currently processing your video',
  [PUSHER_STATES.TRANSCRIPTION_UNAVAILABLE]:
    'Video Processing Failed: There was an issue processing your video. Please contact our support team for assistance.',
  [PUSHER_STATES.TRANSCRIPTION_SUPPORT_RESTRICTED]:
    'Multi language video processing and subtitles is not included in your current plan. Contact Goldcast to upgrade and access this feature.',
  [PUSHER_STATES.TRANSCRIPT_NOT_AVAILABLE]:
    'Video Processing Failed: There was an issue processing your video. Please contact our support team for assistance.',
  [PUSHER_STATES.CLIP_GENERATION_DONE]:
    'Clip Generation Completed! Fresh assets have been generated from your uploaded video.',
  [PUSHER_STATES.CLIP_GENERATION_FAILED]: 'Sorry, we encountered an issue generating clips for the uploaded video.',
  [PUSHER_STATES.CLIP_GENERATION_LIMIT_REACHED]:
    'Clip Generation Failed: You have reached the limit for generating clips from this video.',
  [PUSHER_STATES.DOWNLOAD_DONE]: 'Your clip is ready!',
  [PUSHER_STATES.DOWNLOAD_FAILED]: 'This video failed to download, please try again.',
  [PUSHER_STATES.MULTIMODAL_DOWNLOAD_DONE]: 'Your post is ready!',
  [PUSHER_STATES.MULTIMODAL_DOWNLOAD_FAILED]: 'This post failed to download, please try again.',
  [PUSHER_STATES.SOCIAL_POST_GENERATION_DONE]: 'Social Post Generation Completed!',
  [PUSHER_STATES.BLOG_POST_GENERATION_DONE]: 'Blog Post Generation Completed!',
  [PUSHER_STATES.EMAIL_POST_GENERATION_DONE]: 'Email Post Generation Completed!',
  [PUSHER_STATES.TAKEAWAY_POST_GENERATION_DONE]: 'TakeAway Post Generation Completed!',
  [PUSHER_STATES.FACIAL_RECOGNITION_FAST_PROCESSING]: '',
  [PUSHER_STATES.FACIAL_RECOGNITION_FAST_DONE]: '',
  [PUSHER_STATES.FACIAL_RECOGNITION_FAST_FAILED]:
    'Failed to process clip. Please try moving to a different layout or template.',
  [PUSHER_STATES.FACIAL_RECOGNITION_ACCURATE_PROCESSING]: '',
  [PUSHER_STATES.FACIAL_RECOGNITION_ACCURATE_DONE]: '',
  [PUSHER_STATES.FACIAL_RECOGNITION_ACCURATE_FAILED]:
    'Failed to process clip. Please try moving to a different layout or template',
  [PUSHER_STATES.FTUX_CLIPS_FACIAL_RECOGNITION_DONE]:
    'Clip Generation Completed! Fresh assets have been generated from your uploaded video.',
  [PUSHER_STATES.FACIAL_RECOGNITION_FULLVIDEO_PROCESSING]: '',
  [PUSHER_STATES.FACIAL_RECOGNITION_FULLVIDEO_DONE]: '',
  [PUSHER_STATES.FACIAL_RECOGNITION_FULLVIDEO_FAILED]:
    'Failed to process clip. Please try moving to a different layout or template.',
  [PUSHER_STATES.FTUX_FULLVIDEO_FACIAL_RECOGNITION_DONE]:
    'Clip Generation Completed! Fresh assets have been generated from your uploaded video.',
  [PUSHER_STATES.PUBLISH_STATUS_UPDATED_DONE]: 'Your video is successfully published!',
  [PUSHER_STATES.PUBLISH_STATUS_UPDATED_FAILED]: 'Sorry, we encountered an issue publishing video.',
  [PUSHER_STATES.CHAPTER_GENERATION_DONE]: 'Chapters generated successfully',
  [PUSHER_STATES.CHAPTER_GENERATION_FAILED]: 'Failed to generate chapters. Please try again.',
  [PUSHER_STATES.ALIGNED_INDIVIDUAL_STREAMS_GENERATED_DONE]: 'Individual streams generated successfully',
  [PUSHER_STATES.ALIGNED_INDIVIDUAL_STREAMS_GENERATED_FAILED]:
    'Failed to generate individual streams. Please contact support.',
  [PUSHER_STATES.CAPTION_TRANSLATION_DONE]: 'Caption translation completed',
  [PUSHER_STATES.CAPTION_TRANSLATION_FAILED]: 'Caption translation failed',
  [PUSHER_STATES.AI_AGENT_UPDATE]: 'AI edits are being processed',
  [PUSHER_STATES.EVENT_MIGRATION_IN_PROGRESS]: 'Event migration in progress.',
  [PUSHER_STATES.EVENT_MIGRATION_COMPLETED]: 'Event migration completed.',
  [PUSHER_STATES.SCREENSHARE_DETECTION_DONE]: '',
  [PUSHER_STATES.SCREENSHARE_DETECTION_FAILED]: 'Screenshare detection failed. Video will render without screenshare.',
  [PUSHER_STATES.SCREENSHARE_DETECTION_UNAVAILABLE]: '',
  [PUSHER_STATES.CL_HOMEPAGE_CAROUSEL_STATUS_UPDATED]: ''
};
